import type { ComponentRenderProps } from "@json-render/react";
import { z } from "zod";

import {
  REPORT_DATE_FORMATS,
  ReportDate,
  type ReportDateAlign,
  type ReportDateFormat,
} from "./ReportDate";

const reportDateFormatSchema = z.enum(REPORT_DATE_FORMATS);
const reportDateAlignSchema = z.enum(["left", "right"]);

/** Layout Studio catalog definition for ReportDate. */
export const ReportDateSpec = {
  props: z.object({
    dateFormat: reportDateFormatSchema.nullable().optional(),
    align: reportDateAlignSchema.nullable().optional(),
  }),
  description: `Standalone report date line. The scheduled briefing date is supplied automatically at render time. dateFormat is monthDayYear (default) or dayAbbrevMonthYear; omit for monthDayYear. align is left or right; omit for right. Use only in layouts without a ReportHeader; next to a ReportHeader it is dropped at render because the header already shows the date.`,
  example: {
    dateFormat: "monthDayYear",
    align: "right",
  },
} as const;

type ReportDatePropsFromSpec = {
  dateValue?: string | null;
  dateFormat?: ReportDateFormat | null;
  align?: ReportDateAlign | null;
};

/** Adapter for json-render defineRegistry: renders ReportDate from catalog props. */
export const ReportDateFromProps = ({ element }: ComponentRenderProps) => {
  const { dateValue, dateFormat, align } =
    element.props as ReportDatePropsFromSpec;

  if (dateValue == null || dateValue.trim() === "") {
    return null;
  }

  return (
    <ReportDate
      dateValue={dateValue}
      dateFormat={dateFormat ?? "monthDayYear"}
      align={align ?? "right"}
    />
  );
};
